import React, { useEffect, useState } from "react";

import {
    Button,
    LabeledField,
    LanguageSegmentedControl,
    Modal,
    Stack,
    TextInput,
    Textarea,
} from "shared/ui";

import { MAX_TABLE_NUMBER, MIN_TABLE_NUMBER } from "features/TablesManagement/constants";
import { useTablesManagementContext } from "features/TablesManagement/context";
import { toNumber } from "features/TablesManagement/lib/tableForm";
import { digitsOnly } from "shared/lib/formInput";
import { trimLocalizedMap } from "shared/lib/localizedMap";

import { LangRow, NumberField } from "./styles";

export const TableDialog = () => {
    const state = useTablesManagementContext();
    const { availableLanguages, editedTable, isTableDialogOpen, locale, nextPublicId } = state;

    const [activeLang, setActiveLang] = useState<string>(locale);
    const [publicId, setPublicId] = useState<string>("");
    const [title, setTitle] = useState<Record<string, string>>({});
    const [description, setDescription] = useState<Record<string, string>>({});
    const [externalId, setExternalId] = useState<string>("");

    useEffect(() => {
        if (!isTableDialogOpen) return;

        setActiveLang(locale);
        setPublicId(String(editedTable ? editedTable.publicId : nextPublicId));
        // У нового стола название подставляем сразу — оно печатается на QR-коде.
        setTitle(editedTable ? { ...editedTable.title } : { [locale]: `Стол ${nextPublicId}` });
        setDescription(editedTable ? { ...editedTable.description } : {});
        setExternalId(editedTable?.externalId || "");
    }, [editedTable, isTableDialogOpen, locale, nextPublicId]);

    const number = toNumber(publicId);
    const isInRange =
        number !== null && number >= MIN_TABLE_NUMBER && number <= MAX_TABLE_NUMBER;
    const isTaken =
        number !== null &&
        number !== (editedTable && editedTable.publicId) &&
        state.occupiedNumbers.indexOf(number) !== -1;
    const isNumberValid = isInRange && !isTaken;

    const numberHint = () => {
        if (!isInRange) return `Число от ${MIN_TABLE_NUMBER} до ${MAX_TABLE_NUMBER}`;
        if (isTaken) return "Стол с таким номером уже есть";
        return "Системный номер стола";
    };

    const handleSubmit = () => {
        if (!isNumberValid || number === null) return;

        state.submitTable({
            publicId: number,
            title: trimLocalizedMap(title),
            description: trimLocalizedMap(description),
            externalId: externalId.trim() || null,
        });
    };

    return (
        <Modal
            open={isTableDialogOpen}
            onClose={state.closeTableDialog}
            title={editedTable ? "Редактирование стола" : "Новый стол"}
            actions={
                <>
                    <Button
                        variant="outlined"
                        color="neutral"
                        onClick={state.closeTableDialog}
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!isNumberValid || state.saving}
                        onClick={handleSubmit}
                    >
                        Сохранить
                    </Button>
                </>
            }
        >
            <Stack>
                <NumberField>
                    <LabeledField label="Номер стола" hint={numberHint()}>
                        <TextInput
                            value={publicId}
                            onChange={(value) => setPublicId(digitsOnly(value, 3))}
                            placeholder="12"
                        />
                    </LabeledField>
                </NumberField>

                {availableLanguages.length > 1 && (
                    <LangRow>
                        <LanguageSegmentedControl
                            languages={availableLanguages}
                            activeLang={activeLang}
                            primaryLang={locale}
                            onChange={setActiveLang}
                            isMissing={(lang) => !title[lang]}
                        />
                    </LangRow>
                )}

                <LabeledField label="Название">
                    <TextInput
                        value={title[activeLang]}
                        onChange={(value) =>
                            setTitle({ ...title, [activeLang]: value })
                        }
                        placeholder={`Стол ${publicId || ""}`.trim()}
                    />
                </LabeledField>

                <LabeledField label="Описание" hint="Например, «у окна» или «на террасе»">
                    <Textarea
                        value={description[activeLang]}
                        onChange={(value) =>
                            setDescription({ ...description, [activeLang]: value })
                        }
                        rows={3}
                    />
                </LabeledField>

                <LabeledField
                    label="Внешний ID"
                    hint="Идентификатор стола во внешней системе (например, iiko)"
                >
                    <TextInput
                        value={externalId}
                        onChange={setExternalId}
                        placeholder="Не задан"
                    />
                </LabeledField>
            </Stack>
        </Modal>
    );
};
